import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MatRadioChange } from '@angular/material';
import { AssessmentAnswerInterface, AssessmentQuestionInterface } from 'app/assessment/models';
import { DynamicQuestionComponent } from 'app/assessment/questions/dynamic-question-content/dynamic-question.component';
import { getDeepCopyOfObject } from 'app/shared/utilities/utility-methods.utility';

@Component({
  selector: 'cz-radio-checkbox-group',
  templateUrl: './radio-checkbox-group.component.html',
  styleUrls: ['./radio-checkbox-group.component.scss'],
})
export class RadioCheckboxGroupComponent extends DynamicQuestionComponent implements OnInit, AfterViewInit {
  @Input() public readonly type: string;
  @Input() public readonly question: AssessmentQuestionInterface;
  @Input() public readonly form: FormGroup;
  @Input() public readonly imagePath: string;
  @Input() public readonly parentQuestion: AssessmentQuestionInterface;
  @Input() public readonly readOnly: boolean;
  public followUp: boolean = false;
  public required: boolean;

  constructor() {
    super();
  }

  public ngOnInit():void {
    if (this.question && this.question.childQuestions.length < 1) {
      return;
    }

    this.showFollowUpQuestions();
  }

  public ngAfterViewInit(): void {
    this.required = this.question && this.hasRequiredFields();
  }

  public radioChange(event: MatRadioChange, question: AssessmentQuestionInterface): void {
    if (question.childQuestions.length < 1) {
      this.followUp = false;
      return;
    }

    // loop through question.childQuestionTriggers array to see if question.id is in there, if so, set followUp to true
    const childQuestionsTriggers: number[] = question.childQuestionTriggers;
    for (const key in childQuestionsTriggers) {
      if (!childQuestionsTriggers.hasOwnProperty(key)) { continue; }

      if (childQuestionsTriggers[key] === event.value.id) {
        this.followUp = true;
        this.addRemoveValidators(question, true);
        break;
      } else {
        this.followUp = false;
        this.addRemoveValidators(question, false);
      }
    }
  }

  public isFieldInvalidAndTouched(field: string): boolean {
    return !this.form.get(field).valid && this.form.get(field).touched;
  }

  public displayFieldCss(field: string): {} {
    return {
      'error-msg': this.isFieldInvalidAndTouched(field),
    };
  }

  public onKeyPress(value: string, formGroupName: string, formControlName: string, answer: AssessmentAnswerInterface): void {
    const formGroup: any = this.form.controls[this.type];
    const updatedAnswer: AssessmentAnswerInterface = getDeepCopyOfObject(answer);
    updatedAnswer.text = value;

    if (formGroupName) {
      formGroup.controls[formGroupName].controls[formControlName].setValue(updatedAnswer);
      return;
    }

    formGroup.controls[formControlName].setValue(updatedAnswer);
  }

  public createFormControlName(parentQuestion: AssessmentQuestionInterface, question: AssessmentAnswerInterface): string {
    if (parentQuestion) {
      return `${parentQuestion.id}-${question.id}`;
    }
    return question.id.toString();
  }

  private showFollowUpQuestions(): void {
    const answers: AssessmentAnswerInterface[] = this.question && this.question.answers;
    const childQuestionsTriggers: number[] = this.question && this.question.childQuestionTriggers;

    for (const key in answers) {
      if (!answers.hasOwnProperty(key)) { continue; }

      // find if any answers are selected
      if (answers[key].isSelected) {
        const selectedAnswer: AssessmentAnswerInterface = answers[key];

        for (const key in childQuestionsTriggers) {
          if (!childQuestionsTriggers.hasOwnProperty(key)) { continue; }

          // if the selected answer is a child questions trigger, show the follow up questions
          if (childQuestionsTriggers[key] === selectedAnswer.id) {
            this.followUp = true;
            break;
          } else {
            this.followUp = false;
          }
        }
      }
    }
  }

  private addRemoveValidators(question: AssessmentQuestionInterface, addValidator: boolean): void {
    const childQuestions: AssessmentQuestionInterface[] = question.childQuestions;
    for (const key in childQuestions) {
      if (!childQuestions.hasOwnProperty(key)) { continue; }

      const childQuestion: string = `${this.type}.${this.question.id}-${childQuestions[key].id}`;

      // set validators
      if (addValidator) {
        const control: any = this.form.get(childQuestion);
        if (control instanceof FormGroup) {
          control.setValidators([Validators.required, this.validateChildMulti]);
        } else {
          control.setValidators([Validators.required]);
        }
      } else {
        // remove validators
        this.form.get(childQuestion).clearValidators();
      }

      // update the form group
      this.form.get(childQuestion).updateValueAndValidity();
    }
  }

  // TODO: use the validate method in create-entity-form.ts (saves on some code duplication)
  private validateChildMulti(control: FormGroup): ValidationErrors {
    for (const key in control.controls) {
      if (!control.controls.hasOwnProperty(key)) { continue; }

      const childControl: FormControl = <FormControl>control.controls[key];
      if (childControl.value) {
        return null;
      }
    }

    return {
      validateMultiChoice: false,
    };
  }

  private hasRequiredFields(): boolean {
    const formGroup: AbstractControl = this.form.get(this.type);
    let abstractControl: AbstractControl;

    if (this.question.isMultipleChoice) {
      const nestedFormGroup: AbstractControl = formGroup.get(
        this.parentQuestion ? this.parentQuestion.id.toString() : this.question.id.toString(),
      );
      abstractControl = this.parentQuestion ? nestedFormGroup : nestedFormGroup.get(this.question.answers[0].id.toString());
    } else {
      abstractControl = formGroup.get(this.parentQuestion ? this.parentQuestion.id.toString() : this.question.id.toString());
    }

    if (abstractControl.validator) {
      const validator: any = abstractControl.validator({} as AbstractControl);
      if ((validator && validator.required) || validator === null) {
        return true;
      }
    }
    return false;
  }
}
